
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { useState, useEffect } from "react";
import { X } from "lucide-react";
import CanonicalTag from "@/components/CanonicalTag";
import { NeumorphicContainer } from "@/components/neumorphic";
import NeumorphicThemeToggle from "@/components/NeumorphicThemeToggle";
import { NeumorphicThemeProvider, useNeumorphicTheme } from "@/contexts/NeumorphicThemeContext";

const UDAADUWizardContent = () => {
  const [showCalendly, setShowCalendly] = useState(false);
  const { theme } = useNeumorphicTheme();
  const isDark = theme === "dark";
  
  const openCalendlyModal = () => setShowCalendly(true);
  const closeCalendlyModal = () => setShowCalendly(false);

  // Theme-aware styles
  const pageBackground = isDark ? 'bg-[#0a0a0a]' : 'bg-[#f5f5f5]';
  const textColor = isDark ? 'text-white' : 'text-[#1a1a1a]';
  const subtextColor = isDark ? 'text-gray-300' : 'text-gray-600';
  const recessedStyles = isDark
    ? 'bg-[#0a0a0a] shadow-[inset_8px_8px_16px_#000000,inset_-8px_-8px_16px_#141414]'
    : 'bg-[#f0f0f0] shadow-[inset_8px_8px_16px_#d8d8d8,inset_-8px_-8px_16px_#ffffff]';
  const videoRecessedStyles = isDark
    ? 'shadow-[inset_12px_12px_24px_#000000,inset_-12px_-12px_24px_#141414]'
    : 'shadow-[inset_12px_12px_24px_#c8c8c8,inset_-12px_-12px_24px_#ffffff]';
  const modalBackground = isDark ? 'bg-[#0a0a0a]' : 'bg-white';
  const modalButtonBg = isDark ? 'bg-[#1a1a1a] hover:bg-[#2a2a2a]' : 'bg-white hover:bg-gray-100';
  const modalButtonText = isDark ? 'text-white' : 'text-gray-600';


  return (
    <div className={`min-h-screen transition-colors duration-500 ${isDark ? 'bg-[#0a0a0a] text-white' : 'bg-[#f5f5f5] text-black'}`}>
      <CanonicalTag url="https://www.udaadu.com/uda-adu-wizard" />
      <Navigation />

      {/* Calendly Modal */}
      {showCalendly && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className={`relative w-full max-w-2xl h-[70vh] ${modalBackground} rounded-lg overflow-hidden shadow-xl`}>
            <button 
              onClick={closeCalendlyModal} 
              className={`absolute top-4 right-4 z-10 p-2 ${modalButtonBg} rounded-full shadow-md transition-colors`}
            >
              <X className={`h-6 w-6 ${modalButtonText}`} />
            </button>
            <iframe 
              src="https://calendly.com/udaadu?embed=true&hide_gdpr_banner=1" 
              width="100%" 
              height="100%" 
              frameBorder="0" 
              title="Schedule a meeting" 
            />
          </div>
        </div>
      )}

      <main className={`${pageBackground} transition-all duration-500`}>
        {/* UDA ADU Wizard Page Content */}
        <section className={`max-w-4xl mx-auto px-5 py-16 ${recessedStyles} rounded-2xl my-8 font-sans transition-all duration-500`}>
          <h1 className="text-[#E4683B] text-4xl mb-4">UDA ADU Wizard</h1>
          <p className={`${textColor} text-lg mb-8 transition-colors duration-500`}>
            Discover exactly what's possible on your property — from eligibility and setbacks to design previews and real-time cost estimates.
          </p>

          {/* Hero Video Section */}
          <div className="mb-12 max-w-4xl mx-auto">
            <div className={`aspect-video overflow-hidden rounded-xl ${videoRecessedStyles} ${pageBackground} transition-all duration-500`}>
              <video
                key="hero-video"
                src="https://wrghlmsinvhwvvfhhrvj.supabase.co/storage/v1/object/sign/udavideos/It's%20all%20starting%20from%20here.mp4?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV8zMWY3NTE2YS1mMTNjLTQ2MzEtYTliMC1mOWJmMGFjNTBkZjMiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJ1ZGF2aWRlb3MvSXQncyBhbGwgc3RhcnRpbmcgZnJvbSBoZXJlLm1wNCIsImlhdCI6MTc1MzMzNjA2MCwiZXhwIjoyMDY4Njk2MDYwfQ.XbzgDyg91niAJvV0gkTQ1SyZ8yFeydINg8BvLONMuK4"
                autoPlay
                loop
                muted
                playsInline
                preload="metadata"
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* What Fits My Lot CTA */}
          <div className="mb-12 max-w-2xl text-center">
            <a 
              href="/what-fits-my-lot"
              className="inline-block px-8 py-4 bg-[#E4683B] text-white rounded-xl text-lg font-semibold hover:opacity-90 transition-opacity"
            >
              Explore What Fits Your Lot
            </a>
            <p className={`${subtextColor} text-sm mt-3 transition-colors duration-500`}>
              Learn everything you need to know about your property's ADU potential
            </p>
          </div>

          {/* Feature Details */}
          <div className="mb-8">
            <h3 className="text-[#E4683B] text-xl mb-2">Address Eligibility</h3>
            <p className={`${subtextColor} text-base transition-colors duration-500`}>Enter your address to instantly check if your property qualifies for an ADU based on local zoning and residential classifications.</p>
          </div>

          <div className="mb-8">
            <h3 className="text-[#E4683B] text-xl mb-2">Size Eligibility</h3>
            <p className={`${subtextColor} text-base transition-colors duration-500`}>Determine how large of an ADU you can build on your lot based on coverage ratios, setbacks, and FAR limits.</p>
          </div>

          <div className="mb-8">
            <h3 className="text-[#E4683B] text-xl mb-2">Parcel Information</h3>
            <p className={`${subtextColor} text-base transition-colors duration-500`}>Access publicly available lot data including square footage, lot shape, easements, and overlays.</p>
          </div>

          <div className="mb-8">
            <h3 className="text-[#E4683B] text-xl mb-2">Bird's Eye View</h3>
            <p className={`${subtextColor} text-base transition-colors duration-500`}>Explore a high-resolution satellite and street-level view of your property for design and placement clarity.</p>
          </div>

          <div className="mb-8">
            <h3 className="text-[#E4683B] text-xl mb-2">Drop ADU on Lot</h3>
            <p className={`${subtextColor} text-base transition-colors duration-500`}>Visually position a digital ADU model on your actual property to understand how it will fit in real life.</p>
          </div>

          <div className="mb-8">
            <h3 className="text-[#E4683B] text-xl mb-2">Setbacks & Zoning</h3>
            <p className={`${subtextColor} text-base transition-colors duration-500`}>Auto-calculate required minimum distances from property lines, building separation, and other zoning rules.</p>
          </div>

          <div className="mb-8">
            <h3 className="text-[#E4683B] text-xl mb-2">Slope + Grading</h3>
            <p className={`${subtextColor} text-base transition-colors duration-500`}>Analyze the natural topography of your lot to identify areas that may require retaining walls or grading.</p>
          </div>

          <div className="mb-8">
            <h3 className="text-[#E4683B] text-xl mb-2">3D Visual</h3>
            <p className={`${subtextColor} text-base transition-colors duration-500`}>Generate an interactive 3D rendering of your selected ADU floor plan and visualize it from all angles.</p>
          </div>

          <div className="mb-8">
            <h3 className="text-[#E4683B] text-xl mb-2">Instant Layout</h3>
            <p className={`${subtextColor} text-base transition-colors duration-500`}>See a pre-generated site layout using your address and our AI-driven design engine.</p>
          </div>

          <div className="mb-8">
            <h3 className="text-[#E4683B] text-xl mb-2">Draw Your Own ADU</h3>
            <p className={`${subtextColor} text-base transition-colors duration-500`}>Use our simple drag-and-drop editor to sketch your custom ADU footprint.</p>
          </div>

          <div className="mb-8">
            <h3 className="text-[#E4683B] text-xl mb-2">Use Your Layout</h3>
            <p className={`${subtextColor} text-base transition-colors duration-500`}>Upload existing ADU designs, sketches, or plans to begin planning your project.</p>
          </div>

          <div className="mb-8">
            <h3 className="text-[#E4683B] text-xl mb-2">Our Exclusive Designs</h3>
            <p className={`${subtextColor} text-base transition-colors duration-500`}>Explore UDA ADU's curated library of beautifully designed, pre-approved ADUs ready to build.</p>
          </div>

          <div className="mb-8">
            <h3 className="text-[#E4683B] text-xl mb-2">Real-Time Estimate</h3>
            <p className={`${subtextColor} text-base transition-colors duration-500`}>Get an instant estimate for design + build costs based on your property, finishes, and configuration.</p>
          </div>

          <div className="mb-8">
            <h3 className="text-[#E4683B] text-xl mb-2">And More</h3>
            <p className={`${subtextColor} text-base transition-colors duration-500`}>We're constantly expanding the UDA ADU Wizard — expect permit timelines, rental income calculators, and more.</p>
          </div>
        </section>
      </main>

      <Footer />
      <NeumorphicThemeToggle />
    </div>
  );
};

const UDAADUWizard = () => {
  return (
    <NeumorphicThemeProvider>
      <UDAADUWizardContent />
    </NeumorphicThemeProvider>
  );
};

export default UDAADUWizard;
